import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Boxes,
  Bot,
  BrainCircuit,
  Clock,
  Container,
  GitBranch,
  Layers,
  Lock,
  Rocket,
  ShieldCheck,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Signature } from '@/components/Brand';
import { Badge } from '@/components/ui/Badge';
import { ButtonLink } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { SkeletonCard } from '@/components/ui/Spinner';
import { fetchers, queryKeys } from '@/services/queries';
import type { TrackSummary } from '@/types/api';

const ACCENTS = {
  tech: { ring: 'ring-tech/25', text: 'text-tech', bg: 'bg-tech/10', icon: GitBranch },
  ai: { ring: 'ring-ai/25', text: 'text-ai', bg: 'bg-ai/10', icon: BrainCircuit },
  agent: { ring: 'ring-agent/25', text: 'text-agent', bg: 'bg-agent/10', icon: Bot },
} as const;

type AccentKey = keyof typeof ACCENTS;

function accentOf(track: TrackSummary): AccentKey {
  return (['tech', 'ai', 'agent'] as AccentKey[]).includes(track.accent_color as AccentKey)
    ? (track.accent_color as AccentKey)
    : 'tech';
}

const SKILLS = [
  { icon: Sparkles, title: 'IA appliquée à l’exploitation', text: 'LLM, prompt engineering, RAG et assistants outillés pour le quotidien DevOps.' },
  { icon: Workflow, title: 'CI/CD intelligent', text: 'Jenkins et GitLab CI augmentés : diagnostic automatique des échecs de build.' },
  { icon: Container, title: 'Kubernetes et GitOps', text: 'Docker, Helm, Argo CD et diagnostic de cluster assisté par IA.' },
  { icon: Layers, title: 'MLOps de bout en bout', text: 'Airflow, MLflow, Kubeflow, KServe : de la donnée versionnée au modèle servi.' },
  { icon: Bot, title: 'Agents et multi-agents', text: 'Python, n8n, LangGraph, MCP et CrewAI, avec garde-fous et supervision.' },
  { icon: ShieldCheck, title: 'DevSecOps et sécurité IA', text: 'Trivy, Gitleaks, OWASP LLM, injection de prompt et cloisonnement d’agents.' },
];

const TECHNOLOGIES = [
  'Python', 'Ollama', 'Jenkins', 'GitLab CI', 'Terraform', 'Ansible', 'AWS', 'Docker',
  'Kubernetes', 'Helm', 'Argo CD', 'Prometheus', 'Grafana', 'Airflow', 'MLflow',
  'Kubeflow', 'KServe', 'vLLM', 'DVC', 'MinIO', 'Evidently', 'LangChain', 'LangGraph',
  'n8n', 'MCP', 'CrewAI', 'Qdrant', 'Trivy',
];

const PROJECTS = [
  {
    code: 'A12',
    title: 'KH AI DevOps Assistant',
    text: 'Assistant d’exploitation : analyse de logs, RAG documentaire et déclenchement de pipeline, avec traçabilité.',
    accent: 'tech' as AccentKey,
  },
  {
    code: 'B10',
    title: 'KH MLOps Platform',
    text: 'Plateforme complète : ingestion versionnée, entraînement orchestré, registre, service d’inférence et surveillance de dérive.',
    accent: 'ai' as AccentKey,
  },
  {
    code: 'C06',
    title: 'KH Multi-Agent DevSecOps',
    text: 'Système multi-agents : analyse de dépôt, détection de vulnérabilités, correctif proposé et approbation humaine.',
    accent: 'agent' as AccentKey,
  },
];

export function HomePage() {
  const stats = useQuery({ queryKey: queryKeys.stats, queryFn: fetchers.stats });
  const tracks = useQuery({ queryKey: queryKeys.tracks, queryFn: fetchers.tracks });

  const totalHours = stats.data?.total_hours ?? 252;
  const moduleCount = stats.data?.module_count ?? 28;
  const trackCount = stats.data?.track_count ?? 3;
  const publishedModules = stats.data?.published_module_count;

  return (
    <>
      {/* ---------------------------------------------------------------- Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="kha-grid-backdrop absolute inset-0" aria-hidden="true" />
        <div
          className="absolute -top-40 left-1/2 h-80 w-[55rem] -translate-x-1/2 rounded-full
                     bg-gradient-to-r from-tech/20 via-ai/20 to-agent/20 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <Badge tone="tech" className="mb-6">
              <Sparkles className="h-3 w-3" />
              Formation professionnelle DevOps &amp; Intelligence Artificielle
            </Badge>
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
              KH <span className="kha-gradient-text">AI ACADEMY</span>
            </h1>
            <p className="mt-5 text-lg font-medium text-muted sm:text-xl">
              Apprendre, construire et industrialiser l'intelligence artificielle.
            </p>
            <p className="mx-auto mt-5 max-w-2xl text-balance text-base text-muted">
              Trois parcours professionnels pour les ingénieurs DevOps, Cloud et SRE qui veulent
              intégrer l'IA dans la chaîne de production : assistants de développement, pipelines
              augmentés, AIOps, plateformes MLOps et systèmes multi-agents sécurisés.
            </p>

            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <ButtonLink to="/parcours" size="lg">
                Découvrir les parcours
                <ArrowRight className="h-4 w-4" />
              </ButtonLink>
              <ButtonLink to="/connexion" size="lg" variant="outline">
                Accéder à mon espace
              </ButtonLink>
            </div>

            {/* Key figures */}
            <dl className="mx-auto mt-14 grid max-w-2xl grid-cols-3 gap-4">
              {[
                { value: totalHours, unit: 'heures', label: 'de formation', icon: Clock },
                { value: moduleCount, unit: 'modules', label: 'pédagogiques', icon: Boxes },
                { value: trackCount, unit: 'parcours', label: 'professionnels', icon: Rocket },
              ].map(({ value, unit, label, icon: Icon }) => (
                <div
                  key={unit}
                  className="rounded-card border border-border bg-surface/70 px-3 py-5 backdrop-blur"
                >
                  <Icon className="mx-auto mb-2 h-4 w-4 text-tech" aria-hidden="true" />
                  <dd className="text-3xl font-extrabold tabular-nums text-fg sm:text-4xl">
                    {value}
                  </dd>
                  <dt className="mt-1 text-xs font-semibold uppercase tracking-wide text-faint">
                    {unit}
                    <span className="block font-normal normal-case tracking-normal">{label}</span>
                  </dt>
                </div>
              ))}
            </dl>
            <p className="mt-5 text-sm font-semibold text-muted">
              {totalHours} heures — {moduleCount} modules — {trackCount} parcours professionnels
            </p>
            {publishedModules !== undefined && publishedModules < moduleCount ? (
              <p className="mt-3 text-xs text-faint">
                {publishedModules} module{publishedModules > 1 ? 's' : ''} actuellement
                ouvert{publishedModules > 1 ? 's' : ''} aux inscrits. Les autres sont en cours de
                production et seront ouverts progressivement.
              </p>
            ) : null}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- Tracks */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" aria-labelledby="parcours">
        <div className="mx-auto max-w-2xl text-center">
          <h2 id="parcours" className="text-3xl font-bold tracking-tight">
            Trois parcours complémentaires
          </h2>
          <p className="mt-3 text-muted">
            Du socle IA pour DevOps à l'industrialisation de systèmes multi-agents.
          </p>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {tracks.isLoading
            ? [0, 1, 2].map((index) => <SkeletonCard key={index} />)
            : tracks.data?.map((track) => {
                const accent = ACCENTS[accentOf(track)];
                const Icon = accent.icon;
                return (
                  <Card key={track.id} interactive className="flex flex-col">
                    <CardBody className="flex flex-1 flex-col">
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className={`grid h-11 w-11 place-items-center rounded-xl ${accent.bg} ring-1 ring-inset ${accent.ring}`}
                        >
                          <Icon className={`h-5 w-5 ${accent.text}`} aria-hidden="true" />
                        </span>
                        <Badge tone="neutral">Parcours {track.code}</Badge>
                      </div>

                      <h3 className="mt-4 text-lg font-bold text-fg">{track.title}</h3>
                      {track.subtitle ? (
                        <p className={`mt-1 text-sm font-medium ${accent.text}`}>{track.subtitle}</p>
                      ) : null}
                      <p className="mt-3 line-clamp-4 flex-1 text-sm text-muted">
                        {track.description}
                      </p>

                      <dl className="mt-5 grid grid-cols-3 gap-2 border-t border-border pt-4 text-center">
                        <div>
                          <dd className="text-lg font-bold tabular-nums text-fg">
                            {track.duration_hours}
                          </dd>
                          <dt className="text-2xs text-faint">heures</dt>
                        </div>
                        <div>
                          <dd className="text-lg font-bold tabular-nums text-fg">
                            {track.module_count}
                          </dd>
                          <dt className="text-2xs text-faint">modules</dt>
                        </div>
                        <div>
                          <dd className="text-lg font-bold tabular-nums text-ok">
                            {track.published_module_count}
                          </dd>
                          <dt className="text-2xs text-faint">ouverts</dt>
                        </div>
                      </dl>

                      <Link
                        to={`/parcours/${track.slug}`}
                        className={`mt-5 inline-flex items-center gap-1.5 text-sm font-semibold ${accent.text} hover:gap-2.5 transition-all`}
                      >
                        Voir le programme
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </CardBody>
                  </Card>
                );
              })}
        </div>
      </section>

      {/* ------------------------------------------------------------- Skills */}
      <section
        className="border-y border-border bg-surface/30"
        aria-labelledby="competences"
      >
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 id="competences" className="text-3xl font-bold tracking-tight">
              Compétences développées
            </h2>
            <p className="mt-3 text-muted">
              Des acquis directement applicables en production, pas des concepts théoriques.
            </p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SKILLS.map(({ icon: Icon, title, text }) => (
              <Card key={title} className="p-5">
                <Icon className="h-5 w-5 text-tech" aria-hidden="true" />
                <h3 className="mt-3 font-semibold text-fg">{title}</h3>
                <p className="mt-1.5 text-sm text-muted">{text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- Technologies */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8" aria-labelledby="technos">
        <div className="mx-auto max-w-2xl text-center">
          <h2 id="technos" className="text-3xl font-bold tracking-tight">
            Technologies couvertes
          </h2>
          <p className="mt-3 text-muted">
            Les outils réellement utilisés en entreprise, manipulés en travaux pratiques.
          </p>
        </div>
        <ul className="mx-auto mt-10 flex max-w-4xl flex-wrap justify-center gap-2">
          {TECHNOLOGIES.map((name) => (
            <li
              key={name}
              className="rounded-full border border-border bg-surface px-3.5 py-1.5 text-sm
                         font-medium text-muted transition-colors hover:border-tech hover:text-fg"
            >
              {name}
            </li>
          ))}
        </ul>
      </section>

      {/* ----------------------------------------------------------- Projects */}
      <section
        className="border-y border-border bg-surface/30"
        aria-labelledby="projets-pedagogiques"
      >
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 id="projets-pedagogiques" className="text-3xl font-bold tracking-tight">
              Projets pédagogiques fil rouge
            </h2>
            <p className="mt-3 text-muted">
              Chaque parcours se conclut par un projet complet, soutenu et évalué sur grille.
            </p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {PROJECTS.map((project) => {
              const accent = ACCENTS[project.accent];
              return (
                <Card key={project.code} interactive className="p-5">
                  <div className="flex items-center gap-2">
                    <Badge tone="neutral">{project.code}</Badge>
                    <span className={`text-2xs font-semibold uppercase ${accent.text}`}>
                      Projet final
                    </span>
                  </div>
                  <h3 className="mt-3 font-bold text-fg">{project.title}</h3>
                  <p className="mt-2 text-sm text-muted">{project.text}</p>
                </Card>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <ButtonLink to="/projets" variant="outline">
              Détail des projets
              <ArrowRight className="h-4 w-4" />
            </ButtonLink>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------- CTA */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <Card className="overflow-hidden">
          <div className="relative px-6 py-14 text-center sm:px-12">
            <div
              className="absolute inset-0 bg-gradient-to-br from-tech/10 via-transparent to-ai/10"
              aria-hidden="true"
            />
            <div className="relative">
              <Lock className="mx-auto h-6 w-6 text-tech" aria-hidden="true" />
              <h2 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl">
                Accès réservé aux candidats inscrits
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-muted">
                Les cours, travaux pratiques et quiz sont accessibles depuis votre espace personnel.
                La progression, les scores et les corrections sont gérés côté serveur.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <ButtonLink to="/connexion" size="lg">
                  Se connecter
                </ButtonLink>
                <ButtonLink to="/parcours" size="lg" variant="outline">
                  Consulter le catalogue
                </ButtonLink>
              </div>
              <Signature className="mt-10" />
            </div>
          </div>
        </Card>
      </section>
    </>
  );
}
